import { useState } from 'react';

// Gemeinsame Chart-Bausteine für „Blechstärke × Format“ (Blechbedarf-Diagramme + Werkstoff-Analyse):
//  · StapelBalken — eine Zeile je Blechstärke, Segmente = Formate (Top 6 + „Weitere“)
//  · FormatDonut  — Aufteilung der Formate innerhalb EINER Blechstärke
//
// Farben sind FEST je Format (Hamdi 15.10.2026: „GF hep mavi olsun“) — egal wie gefiltert oder sortiert wird:
//   GF = Blau · MAXI (inkl. MX) = Orange · MF = Grün · KF = Gelb · ohne Angabe (—) = Rosa
// Seltene Formate bekommen eine feste Zusatzfarbe aus ihrem Namen (gleicher Name → gleiche Farbe);
// alles darüber hinaus ist „Weitere“ (Grau). Reihenfolge in Legende und Balken ist ebenfalls fest.

type Farbe = { bg: string; fg: string };
export const FORMAT_FIX: Record<string, Farbe> = {
  GF:   { bg: '#2a78d6', fg: '#ffffff' },
  MAXI: { bg: '#eb6834', fg: '#ffffff' },
  MF:   { bg: '#1baf7a', fg: '#0b0b0b' },
  KF:   { bg: '#eda100', fg: '#0b0b0b' },
  '':   { bg: '#e87ba4', fg: '#0b0b0b' },
  '—':  { bg: '#e87ba4', fg: '#0b0b0b' },   // falls „ohne Angabe“ als Strich statt leer ankommt
};
export const FIX_REIHENFOLGE = ['GF', 'MAXI', 'MF', 'KF', '', '—'] as const;
// Zusatzfarben für seltene Formate — nur zwei, damit alles unterscheidbar bleibt
// (validate_palette: Reihenfolge GF·MAXI·MF·KF·—·Grün·Lila besteht Helligkeit, Chroma, CVD, Normalsicht)
const FREI: Farbe[] = [{ bg: '#008300', fg: '#ffffff' }, { bg: '#7c4dbd', fg: '#ffffff' }];
export const WEITERE = '#b9b8b2';
export const MAX_FARBEN = 6;
/** @deprecated nur noch für ältere Aufrufer: Farben der festen Formate in fester Reihenfolge */
export const FORMAT_FARBEN = FIX_REIHENFOLGE.map(k => FORMAT_FIX[k]!.bg);

export type FormatInfo = { key: string; label: string; n: number };
export type StapelZeile = { key: string; label: string; n: number; segs: { fk: string; n: number }[] };
export type GeplantesFormat = FormatInfo & Farbe & { idx: number };

function hash(t: string): number { let h = 0; for (const c of t) h = (h * 31 + c.charCodeAt(0)) >>> 0; return h; }

/** Welche Formate bekommen eine Farbe, in welcher Reihenfolge? Feste zuerst (feste Reihenfolge), dann die häufigsten übrigen. */
export function farbPlan(formate: FormatInfo[]): GeplantesFormat[] {
  const fix = FIX_REIHENFOLGE.map(k => formate.find(f => f.key === k)).filter((f): f is FormatInfo => !!f);
  const rest = formate.filter(f => !(f.key in FORMAT_FIX)).sort((a, b) => b.n - a.n || a.label.localeCompare(b.label, 'de'))
    .slice(0, Math.min(FREI.length, Math.max(0, MAX_FARBEN - fix.length)));
  const belegt = new Set<number>();
  const frei = rest.map(f => {
    let i = hash(f.key) % FREI.length;
    for (let n = 0; belegt.has(i) && n < FREI.length; n++) i = (i + 1) % FREI.length;   // Kollision im selben Bild vermeiden
    belegt.add(i);
    return { ...f, ...FREI[i]! };
  });
  return [...fix.map(f => ({ ...f, ...FORMAT_FIX[f.key]! })), ...frei].map((f, idx) => ({ ...f, idx }));
}

export function formatFarbe(formate: FormatInfo[], fk: string): { bg: string; fg: string; idx: number } {
  const p = farbPlan(formate).find(f => f.key === fk);
  return p ? { bg: p.bg, fg: p.fg, idx: p.idx } : { bg: WEITERE, fg: '#0b0b0b', idx: -1 };
}

const mm = (label: string, einheit: string) => (label === '—' ? label : `${label} ${einheit}`);

export function StapelBalken({ titel, sub, zeilen, formate, gesamt, zeileGew, formatGew, onZeile, onSeg, onFormat, einheit = 'mm', zeilenName = 'Blechstärke', kurz = 10 }: {
  titel: string; sub: string; zeilen: StapelZeile[]; formate: FormatInfo[]; gesamt: number;
  zeileGew: string | null; formatGew: string | null;
  onZeile: (zeileKey: string) => void; onSeg: (zeileKey: string, fk: string) => void; onFormat: (fk: string) => void;
  einheit?: string; zeilenName?: string; kurz?: number;
}) {
  const [sort, setSort] = useState<'haeufig' | 'mm'>('haeufig');
  const [alle, setAlle] = useState(false);
  const anteil = (n: number) => (gesamt ? Math.round((n / gesamt) * 100) : 0);
  const sortiert = [...zeilen].sort((a, b) => sort === 'mm'
    ? (Number(a.key.replace(',', '.')) || 1e9) - (Number(b.key.replace(',', '.')) || 1e9) || a.label.localeCompare(b.label, 'de')
    : b.n - a.n || a.label.localeCompare(b.label, 'de'));
  const sichtbar = alle ? sortiert : sortiert.slice(0, kurz);
  const maxN = Math.max(1, ...sichtbar.map(z => z.n));
  const top = farbPlan(formate);
  const imPlan = new Set(top.map(f => f.key));
  const weitereN = formate.filter(f => !imPlan.has(f.key)).reduce((s, f) => s + f.n, 0);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="staebe">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="font-semibold text-slate-900 text-[15px]">{titel}</h2>
          <div className="text-xs text-slate-500">{sub}</div>
        </div>
        <div className="flex gap-0.5 border border-slate-200 rounded-lg p-0.5 text-xs" role="group" aria-label="Sortierung">
          {([['haeufig', 'nach Häufigkeit'], ['mm', `nach ${einheit}`]] as const).map(([v, l]) => (
            <button key={v} type="button" data-testid={`sort-${v}`} aria-pressed={sort === v} onClick={() => setSort(v)}
                    className={`px-2.5 py-1 rounded ${sort === v ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Legende (≥ 2 Serien): Farbe = Format, mit Anzahl; anklickbar = nur dieses Format */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mb-4 text-[13px]" data-testid="legende">
        {top.map(f => {
          const gew = formatGew === f.key;
          return (
            <button key={f.key || '—'} type="button" disabled={f.label === '—'} data-testid={`legende-${f.key || 'leer'}`} aria-pressed={gew}
                    onClick={() => onFormat(f.key)} title={`${f.label}: ${f.n} (${anteil(f.n)} %)`}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 ${gew ? 'border-sky-400 bg-sky-50' : 'border-slate-200 hover:bg-slate-50'}`}>
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: f.bg }} />
              <span className="text-slate-800 font-semibold">{f.label}</span>
              <span className="text-slate-500 tabular-nums">{f.n}</span>
            </button>
          );
        })}
        {formate.length > top.length && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-0.5" title={`${formate.length - top.length} weitere Formate`}>
            <span className="inline-block w-3 h-3 rounded-full" style={{ background: WEITERE }} />
            <span className="text-slate-800 font-semibold">Weitere</span>
            <span className="text-slate-500 tabular-nums">{weitereN}</span>
          </span>
        )}
      </div>

      <div className="space-y-1.5">
        {sichtbar.map(z => {
          const zGew = zeileGew === z.key;
          const sperre = z.label === '—';
          const rang = new Map(top.map(f => [f.key, f.idx]));   // feste Reihenfolge: GF, MAXI, MF, KF, —, dann übrige
          const segs = z.segs.filter(s => s.n > 0 && rang.has(s.fk)).sort((a, b) => rang.get(a.fk)! - rang.get(b.fk)!);
          const rest = z.n - segs.reduce((s, x) => s + x.n, 0);
          return (
            <div key={z.key || '—'} className={`flex items-center gap-2 rounded-lg px-1.5 py-0.5 ${zGew ? 'bg-sky-50 ring-1 ring-sky-200' : ''}`} data-testid={`zeile-${z.key || 'leer'}`}>
              <button type="button" disabled={sperre} data-testid={`label-${z.key || 'leer'}`} aria-pressed={zGew} onClick={() => onZeile(z.key)}
                      className="w-[4.5rem] shrink-0 text-right text-sm font-bold text-slate-800 hover:underline disabled:no-underline disabled:cursor-default"
                      title={`nur ${mm(z.label, einheit)} (${z.n})`}>{z.label}{!sperre && <span className="font-normal text-slate-400 text-xs"> {einheit}</span>}</button>
              <div className="flex-1 min-w-0 h-7">
                <div className="flex h-full gap-[2px]" style={{ width: `${(z.n / maxN) * 100}%`, minWidth: 8 }}>
                  {segs.map((s, si) => {
                    const c = formatFarbe(formate, s.fk);
                    const gew = zGew && formatGew === s.fk;
                    const lab = formate.find(f => f.key === s.fk)?.label ?? '—';
                    return (
                      <button key={s.fk || '—'} type="button" disabled={lab === '—' || sperre} data-testid={`seg-${z.key || 'leer'}-${s.fk || 'leer'}`}
                              onClick={() => onSeg(z.key, s.fk)} title={`${mm(z.label, einheit)} · ${lab}: ${s.n} (${anteil(s.n)} %)`}
                              className={`h-full text-xs font-bold tabular-nums leading-none hover:brightness-110 transition ${si === 0 ? 'rounded-l-md' : ''} ${si === segs.length - 1 && rest <= 0 ? 'rounded-r-md' : ''} ${gew ? 'ring-2 ring-slate-900 ring-offset-1 z-10' : ''}`}
                              style={{ flexGrow: s.n, flexBasis: 0, background: c.bg, color: c.fg }}>
                        {s.n / maxN >= 0.06 && s.n >= 3 ? s.n : ''}
                      </button>
                    );
                  })}
                  {rest > 0 && (
                    <button type="button" disabled={sperre} data-testid={`seg-${z.key || 'leer'}-weitere`} onClick={() => onZeile(z.key)}
                            title={`${mm(z.label, einheit)} · weitere Formate: ${rest} (${anteil(rest)} %)`}
                            className={`h-full rounded-r-md hover:brightness-95 ${segs.length === 0 ? 'rounded-l-md' : ''}`}
                            style={{ flexGrow: rest, flexBasis: 0, background: WEITERE }} />
                  )}
                </div>
              </div>
              <span className="w-16 shrink-0 text-sm font-semibold tabular-nums text-slate-900" title={`${z.n} Positionen`}>{z.n} <span className="text-[11px] font-normal text-slate-400">{anteil(z.n)} %</span></span>
            </div>
          );
        })}
      </div>
      {zeilen.length > kurz && (
        <button type="button" data-testid="zeilen-mehr" onClick={() => setAlle(a => !a)} className="mt-3 text-sm text-brand underline">
          {alle ? 'weniger anzeigen' : `alle ${zeilen.length} ${zeilenName}n anzeigen`}
        </button>
      )}
    </div>
  );
}

// Donut: Anteile der Formate innerhalb einer Blechstärke; in der Mitte die Gesamtzahl
export function FormatDonut({ segmente, mitte, mitteText, groesse = 176 }: {
  segmente: { key: string; label: string; n: number; farbe: string }[]; mitte: string | number; mitteText: string; groesse?: number;
}) {
  const r = 64, breite = 24, C = 2 * Math.PI * r;
  const gesamt = segmente.reduce((s, x) => s + x.n, 0) || 1;
  let offset = 0;
  return (
    <div className="relative shrink-0" style={{ width: groesse, height: groesse }} data-testid="donut" role="img"
         aria-label={`${mitteText}: ${segmente.map(s => `${s.label} ${s.n}`).join(', ')}`}>
      <svg viewBox="0 0 160 160" width={groesse} height={groesse}>
        <circle cx="80" cy="80" r={r} fill="none" stroke="#f1f5f9" strokeWidth={breite} />
        {segmente.map(s => {
          const len = (s.n / gesamt) * C;
          const gap = segmente.length > 1 ? 2.5 : 0;
          const el = (
            <circle key={s.key || '—'} cx="80" cy="80" r={r} fill="none" stroke={s.farbe} strokeWidth={breite}
                    strokeDasharray={`${Math.max(len - gap, 0.5)} ${C}`} strokeDashoffset={-offset} transform="rotate(-90 80 80)">
              <title>{`${s.label}: ${s.n} (${Math.round((s.n / gesamt) * 100)} %)`}</title>
            </circle>
          );
          offset += len;
          return el;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="text-3xl font-bold text-slate-900 leading-none tabular-nums">{mitte}</div>
        <div className="text-[11px] text-slate-500 mt-1">{mitteText}</div>
      </div>
    </div>
  );
}
